/**
 * 「这次带给 AI 的原文」—— 目的地展示用(2026-08-13)。
 *
 * ═══ 为什么要有这个 ═══════════════════════════════════════════════════════
 * 用户报「五个目的地都没有显示实际传给地基的值」。实情是:结构层**真的传过去了**
 * (payload → ai `formula_card_from_payload` → `FormulaCard.prompt_block()` 注入架构师),
 * 但它走的是一条界面上完全看不见的通道 —— 目的地只显示一个套路名。
 *
 * 引擎真正读到的就是 `prompt_block()` 拼出来的那段人话。所以展示它 = 所见即所送,
 * 不必另设计一套抽象面板。
 *
 * ★单源纪律:优先用 ai 随 payload 带来的 `promptPreview`(2026-08-13 起)。
 *   本地这份 `buildPromptPreview` **只给存量蓝图兜底** —— 它必然与 ai 侧有漂移风险,
 *   所以只在 payload 没带时才用,且不做任何 ai 侧没有的加工。
 */
import type { FormulaPayload } from "./types";

/** 与 ai `library._SLOT_PAT` 同规:1~12 字的 {占位符}。 */
const SLOT_RE = /\{([^{}]{1,12})\}/g;

/** 从文本里按出现顺序提取去重的槽位名(ai `derive_slots` 的前端等价实现)。 */
export function deriveSlots(...texts: (string | undefined | null)[]): string[] {
  const seen: string[] = [];
  for (const t of texts) {
    if (!t) continue;
    for (const m of t.matchAll(SLOT_RE)) {
      if (m[1] && !seen.includes(m[1])) seen.push(m[1]);
    }
  }
  return seen;
}

/**
 * 兜底拼装(仅存量蓝图):逐行对齐 ai `FormulaCard.prompt_block()`。
 *
 * ★行序与措辞刻意与 ai 侧保持一致 —— 用户对着两边看时不该发现差异。
 * ★槽位那行:存量 payload 里没有 slots(2026-08-13 前 ai 侧硬写空),这里按同一套规则
 *   从 copyFormula/beats 现场派生 —— 否则老蓝图会少一行,看起来像「这条没带槽位」。
 */
export function buildPromptPreview(payload: FormulaPayload | null | undefined): string {
  if (!payload) return "";
  if (typeof payload.promptPreview === "string" && payload.promptPreview.trim()) {
    return payload.promptPreview;
  }
  const name = payload.name ?? "爆款追爆套路";
  const lines: string[] = [`【本片套路(系统选定):${name}】`];
  if (payload.when) lines.push(`- 适用:${payload.when}`);
  if (payload.beats15Raw) {
    lines.push(`- 15s 节拍表(结构骨架;系统另发本片实际时长的秒级预算):${payload.beats15Raw}`);
  }
  if (payload.beats30Raw) lines.push(`- 30s 节拍表(片长 ≥30s 时按这张):${payload.beats30Raw}`);
  if (payload.copyFormula) lines.push(`- 口播公式:${payload.copyFormula}`);
  const slots =
    Array.isArray(payload.slots) && payload.slots.length > 0
      ? payload.slots
      : deriveSlots(payload.copyFormula, payload.beats15Raw, payload.beats30Raw);
  if (slots.length > 0) {
    lines.push(
      `- 槽位(从用户素材/租户事实实例化,缺槽用具体近义信息补,不得编造数字):${slots.join("、")}`
    );
  }
  if (payload.pitfalls) lines.push(`- 本套路的坑(必须避开):${payload.pitfalls}`);
  if (payload.cta) lines.push(`- CTA 形态:${payload.cta}`);
  return lines.join("\n");
}

/** 风格适配(2026-08-13 起 ai 侧才有;存量蓝图恒空 → 调用方不渲染那一行)。 */
export function styleAffinityOf(payload: FormulaPayload | null | undefined): {
  fit: string[];
  conflict: string[];
} {
  const pick = (v: unknown) =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && !!x.trim()) : [];
  return { fit: pick(payload?.fit), conflict: pick(payload?.conflict) };
}
